import { useEffect, useRef, useState } from 'react';
import { MapPin, Clock, Gauge, X, RotateCcw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '@/state/gameStore';
import { useViewport } from '@/hooks/useViewport';
import { formatClock, formatShortDate } from '@/lib/format';
import { MODE_LABELS } from '@/lib/saveGame';
import { getRadiusKm } from '@/data/leveling';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';

/**
 * Floating glass card, top-left. Mirrors TopBarFloating styling.
 * Shows Stadt | Zeit | Modus. The Stadt cell opens a popover with
 * coordinates + radius + "Spiel neu starten" link.
 */
export function InfoCard() {
  const homeCity = useGameStore((s) => s.homeCity);
  const gameTime = useGameStore((s) => s.gameTime);
  const mode = useGameStore((s) => s.mode);
  const level = useGameStore((s) => s.level);
  const timeFormat = useGameStore((s) => s.uiPrefs.timeFormat);
  const resetGame = useGameStore((s) => s.resetGame);
  const pushToast = useGameStore((s) => s.pushToast);
  const viewport = useViewport();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  // Click-outside to close
  useEffect(() => {
    if (!popoverOpen) return;
    const onDown = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setPopoverOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPopoverOpen(false);
    };
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [popoverOpen]);

  if (!homeCity) return null;
  // Mobile uses the burger-menu "Spielinfo" entry instead of the top-left card.
  if (viewport === 'mobile') return null;

  const radiusKm = getRadiusKm(level);
  const { lat, lng } = homeCity.center;

  return (
    <>
      <div
        className={[
          'pointer-events-auto fixed left-4 top-4 z-30 flex items-stretch overflow-visible',
          'rounded-2xl border border-white/5 backdrop-blur-md shadow-xl',
        ].join(' ')}
        style={{ background: 'rgba(20, 24, 36, 0.9)' }}
      >
        {/* Stadt (clickable) */}
        <div className="relative" ref={popoverRef}>
          <button
            type="button"
            onClick={() => setPopoverOpen((o) => !o)}
            className="flex items-center gap-2.5 px-4 py-2.5 transition hover:bg-white/5 focus:outline-none focus-visible:bg-white/5"
            aria-expanded={popoverOpen}
            aria-haspopup="dialog"
          >
            <MapPin className="h-5 w-5 flex-shrink-0 text-accent-dispatch" aria-hidden />
            <div className="flex flex-col leading-tight">
              <span className="hidden text-[10px] uppercase tracking-wider text-fg-muted md:inline">
                Stadt
              </span>
              <span className="block max-w-[140px] truncate font-display text-sm font-bold text-fg-primary">
                {homeCity.displayName.split(',')[0]}
              </span>
            </div>
          </button>

          <AnimatePresence>
            {popoverOpen && (
              <motion.div
                role="dialog"
                aria-label="Stadt-Informationen"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.15 }}
                className="absolute left-0 top-full z-40 mt-2 w-72 rounded-xl border border-border-strong bg-bg-secondary p-4 shadow-2xl"
              >
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div>
                    <p className="font-display text-sm font-semibold text-fg-primary">
                      {homeCity.displayName}
                    </p>
                    <p className="mt-0.5 text-xs text-fg-muted">
                      {homeCity.state || homeCity.country}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setPopoverOpen(false)}
                    aria-label="Schließen"
                    className="flex h-6 w-6 items-center justify-center rounded text-fg-muted hover:text-fg-primary"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </div>

                <dl className="space-y-1.5 border-t border-border-subtle pt-3 text-xs">
                  <Row label="Koordinaten">
                    <span className="tabular-nums text-fg-primary">
                      {lat.toFixed(4)}, {lng.toFixed(4)}
                    </span>
                  </Row>
                  <Row label="Aktueller Radius">
                    <span className="tabular-nums text-fg-primary">{radiusKm} km</span>
                  </Row>
                  <Row label="Level">
                    <span className="text-fg-primary">{level}</span>
                  </Row>
                </dl>

                <button
                  type="button"
                  onClick={() => {
                    setPopoverOpen(false);
                    setConfirmOpen(true);
                  }}
                  className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-md border border-accent-emergency/40 bg-accent-emergency/10 px-3 py-2 text-xs font-semibold text-accent-emergency transition hover:bg-accent-emergency/15"
                >
                  <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                  Spiel neu starten
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <Divider />

        {/* Zeit */}
        <div className="flex items-center gap-2.5 px-4 py-2.5">
          <Clock className="h-5 w-5 flex-shrink-0 text-fg-muted" aria-hidden />
          <div className="flex flex-col leading-tight">
            <span className="hidden text-[10px] uppercase tracking-wider text-fg-muted md:inline">
              Zeit
            </span>
            <span className="font-display text-sm font-bold tabular-nums text-fg-primary">
              {formatShortDate(gameTime)} {formatClock(gameTime, timeFormat)}
            </span>
          </div>
        </div>

        <Divider />

        {/* Modus */}
        <div className="flex items-center gap-2.5 px-4 py-2.5">
          <Gauge className="h-5 w-5 flex-shrink-0 text-fg-muted" aria-hidden />
          <div className="flex flex-col leading-tight">
            <span className="hidden text-[10px] uppercase tracking-wider text-fg-muted md:inline">
              Modus
            </span>
            <span className="font-display text-sm font-bold text-fg-primary">
              {MODE_LABELS[mode]}
            </span>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Spiel neu starten?"
        description="Alle Wachen, Fahrzeuge und Einsätze gehen verloren. Modus und Stadtwahl werden zurückgesetzt."
        confirmLabel="Neu starten"
        tone="danger"
        onConfirm={() => {
          resetGame();
          setConfirmOpen(false);
          pushToast({ kind: 'info', text: 'Spielstand zurückgesetzt.' });
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <dt className="text-fg-muted">{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function Divider() {
  return <span aria-hidden className="my-2 w-px bg-white/10" />;
}
